import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Link, Stack, Typography } from '@mui/material';

import { pode } from '../../authorization/pode';
import { useVeredito } from '../../authorization/useVeredito';
import type { AlcanceDaUnidade } from '../../types/unit';

interface FaixaDeAlcanceProps {
  alcance: AlcanceDaUnidade;
}

function contagem(quantidade: number, singular: string, plural: string): string {
  return `${quantidade} ${quantidade === 1 ? singular : plural}`;
}

/**
 * Quem a aplicação à unidade vai alcançar, dito antes de aplicar (#541).
 *
 * A unidade chega ao funcionário pelo setor. Sem setor ligado, a aplicação é gravada e não
 * alcança ninguém — por isso a faixa vira aviso e oferece o caminho para ligar, sem bloquear.
 * E quem tem a unidade no cadastro mas está em setor de outra unidade fica de fora, o que só se
 * descobre se a faixa disser.
 */
export const FaixaDeAlcance: React.FC<FaixaDeAlcanceProps> = ({ alcance }) => {
  const ligarSetores = useVeredito('/departments', pode('department.update'));
  const semSetor = alcance.setores === 0;
  const fora = alcance.funcionariosForaDoAlcance;

  return (
    <Alert severity={semSetor ? 'warning' : 'info'}>
      <Stack spacing={0.5}>
        {semSetor ? (
          <Typography variant="body2">
            Nenhum setor está ligado a esta unidade, então o que for aplicado aqui ainda não alcança
            ninguém.
            {ligarSetores.liberado && (
              <>
                {' '}
                <Link component={RouterLink} to="/departments">
                  Ligar setores
                </Link>
              </>
            )}
          </Typography>
        ) : (
          <Typography variant="body2">
            Vale para <strong>{contagem(alcance.setores, 'setor', 'setores')}</strong>,{' '}
            <strong>{contagem(alcance.cargos, 'cargo', 'cargos')}</strong> e{' '}
            <strong>{contagem(alcance.funcionarios, 'funcionário', 'funcionários')}</strong> desta
            unidade.
          </Typography>
        )}
        {fora > 0 && (
          <Typography variant="body2" color="text.secondary">
            {fora === 1
              ? '1 funcionário tem esta unidade no cadastro, mas está em setor de outra unidade e não recebe estas aplicações.'
              : `${fora} funcionários têm esta unidade no cadastro, mas estão em setor de outra unidade e não recebem estas aplicações.`}
          </Typography>
        )}
      </Stack>
    </Alert>
  );
};
